import React, { ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'dark-utility' | 'pearl' | 'gradient';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center whitespace-nowrap transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer active:scale-95 select-none tracking-tight';

    const variants = {
      primary: 'bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-medium shadow-xs',

      secondary:
        'bg-secondary hover:bg-secondary/80 text-secondary-foreground border border-border rounded-full font-medium',

      outline:
        'bg-card border border-border hover:bg-muted text-foreground rounded-full font-normal shadow-xs',

      ghost: 'bg-transparent hover:bg-muted text-foreground rounded-full font-normal',

      danger: 'bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-full font-medium',

      'dark-utility': 'bg-foreground text-background rounded-lg text-[14px] font-normal hover:opacity-90',

      pearl: 'bg-muted text-foreground border border-border rounded-lg hover:bg-secondary',

      // Backward compatible alias to primary
      gradient: 'bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-medium'
    };

    const sizes = {
      sm: 'text-[13px] px-3.5 py-1.5 min-h-[36px] gap-1.5',
      md: 'text-[14px] px-4 py-2 min-h-[40px] gap-2',
      lg: 'text-[15px] px-6 py-2.5 min-h-[44px] gap-2 font-medium',
      icon: 'p-2.5 min-h-[44px] min-w-[44px] rounded-full'
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={twMerge(clsx(baseStyles, variants[variant], sizes[size], className))}
        {...props}
      >
        {isLoading ? (
          <svg className="animate-spin h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          leftIcon
        )}
        {children}
        {!isLoading && rightIcon}
      </button>
    );
  }
);
Button.displayName = 'Button';
